import * as React from "react";
import { Search } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { Input } from "./Input.jsx";

/**
 * Campo de búsqueda: la lupa, el rótulo y el aspa viven DENTRO de la caja.
 *
 * Envuelve `<Input/>` en vez de re-escribir la caja, y eso es todo el punto de
 * que exista. La versión hecha a mano —un `<div class="bg-surface-1 rounded-lg
 * px-3 py-2">` con un `<input class="bg-transparent">` adentro— se repetía en
 * cuatro sitios del módulo de Revisiones y se quedó fuera del pase de
 * profundidad (PR #664): sin borde, sin `shadow-well` y sin anillo de foco.
 *
 * Lo peor no era la sombra sino el BORDE. La caja se pintaba `bg-surface-1`
 * dentro de un `<SlidePanel/>` o una `<Card/>`, que también son `surface-1`:
 * fondo contra el mismo fondo, sin borde que los separe. En el tema claro el
 * buscador desaparecía — WCAG 1.4.11 pide 3:1 para identificar un control y ahí
 * había 1:1. Heredando de `Input` sale con `border-border-strong` y hundido,
 * que es la física correcta de un campo: RECIBE, no sobresale.
 *
 * **El buscador es la excepción a icono·etiqueta·control de lado** (§ Campos de
 * CLAUDE.md): acá el icono y el rótulo van adentro. Estuvo con el `Field`
 * horizontal —lupa y «Buscar» sueltos a la izquierda— y en la toolbar de una
 * tabla eso son ~70 px de cromo comiéndose la caja antes de que empiece, en la
 * única fila donde el ancho es el que se escribe. La caja de búsqueda con lupa
 * adentro se reconoce sin rótulo: es el patrón que el usuario ya trae de
 * cualquier otra aplicación, y no hay ambigüedad posible sobre qué se teclea
 * ahí. Lo que NO se toca es el nombre accesible: el `<label for>` sigue puesto
 * —`sr-only`, no un `aria-label`— así que el lector de pantalla y los tests por
 * rol lo siguen encontrando por su nombre. El rótulo visible pasa a vivir en el
 * `placeholder`, que por eso EMPIEZA POR EL VERBO: «Buscar placa o conductor…».
 *
 * El aspa solo aparece con texto, y con `type="text"`: en `type="search"` el
 * navegador dibuja la suya y saldrían dos (el propio `Input` ya apaga la nativa
 * de WebKit, pero Firefox no la expone igual).
 *
 * `trailing` mete un control MÁS dentro de la caja, a la derecha del aspa: hoy
 * es el disparador del panel de filtros. La caja de búsqueda pasa a ser la
 * barra entera de "qué veo de esta tabla" —buscar y recortar en un solo
 * control— en vez de una caja y una píldora vecinas diciendo lo mismo. El
 * relleno derecho del campo se MIDE contra lo que haya ahí: el disparador
 * cambia de ancho solo (el rótulo se esconde bajo `md`, y con filtros puestos
 * le sale una chapita con el número), así que un `pr-*` fijo dejaría el texto
 * metiéndose debajo justo cuando hay algo filtrado.
 *
 * @param {string} value
 * @param {(v: string) => void} onValueChange — recibe el string, no el evento.
 * @param {string} label — nombre accesible del campo (no se pinta).
 * @param {import('react').ReactNode} [trailing] — control anclado dentro de la
 *   caja, a la derecha. Se le reserva sitio midiendo, no a ojo.
 */
const SearchInput = React.forwardRef(
  (
    {
      value,
      onValueChange,
      label,
      placeholder = "Buscar…",
      clearLabel = "Limpiar búsqueda",
      trailing = null,
      className,
      id,
      ...props
    },
    ref,
  ) => {
    // `useId` para poder ligar `<label for>` sin obligar a cada caller a
    // inventar un id, pero se respeta el que venga por props.
    //
    // Se le quitan los dos puntos que `useId` mete (`:r1:`): como id son
    // válidos y `label[for]` los liga bien —compara cadenas—, pero rompen
    // cualquier `querySelector('#…')`, que los lee como pseudo-clase y lanza
    // SyntaxError en vez de no encontrar nada. Un fallo que no se parece a su
    // causa, y que aparecería en un test o un script, no acá.
    const autoId = React.useId().replace(/:/g, "");
    const inputId = id ?? `search-${autoId}`;

    // Relleno derecho del campo = lo que ocupa la botonera de adentro + aire.
    // Se mide en vez de estimarse porque el contenido cambia de ancho en
    // marcha: el rótulo «Filtros» aparece en `md`, y la chapita del contador
    // sale y entra al filtrar. Sin `trailing` no hay nada que medir y vale el
    // `pr-9` de siempre (el hueco del aspa).
    const botoneraRef = React.useRef(null);
    const [padding, setPadding] = React.useState(null);
    React.useLayoutEffect(() => {
      const el = botoneraRef.current;
      if (!el || !trailing) {
        setPadding(null);
        return undefined;
      }
      const medir = () => setPadding(Math.ceil(el.getBoundingClientRect().width) + 12);
      medir();
      const ro =
        typeof ResizeObserver === "undefined" ? null : new ResizeObserver(medir);
      ro?.observe(el);
      return () => ro?.disconnect();
    }, [trailing]);

    return (
      <div className={cn("relative min-w-0", className)}>
        <label htmlFor={inputId} className="sr-only">
          {label}
        </label>
        <Search
          aria-hidden="true"
          strokeWidth={1.75}
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
        />
        <Input
          ref={ref}
          id={inputId}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onValueChange?.(e.target.value)}
          // `pr-9` siempre, no solo con texto: si el relleno cambiara al
          // aparecer el aspa, el texto ya escrito daría un salto lateral al
          // teclear. `pl-9` deja sitio a la lupa.
          className="pl-9 pr-9"
          style={padding === null ? undefined : { paddingRight: `${padding}px` }}
          {...props}
        />
        <div
          ref={botoneraRef}
          className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center"
        >
          {/* El hueco del aspa está SIEMPRE, con o sin texto: si se colapsara
              al vaciar la caja, lo de al lado se correría a la derecha y el
              texto daría un salto al teclear la primera letra. */}
          <span className="flex h-7 w-7 shrink-0 items-center justify-center">
            {value && (
              <button
                type="button"
                onClick={() => onValueChange?.("")}
                aria-label={clearLabel}
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-md",
                  "text-text-dim transition-colors hover:bg-surface-2 hover:text-text-primary",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                )}
              >
                <span className="material-symbols-outlined text-base" aria-hidden="true">
                  close
                </span>
              </button>
            )}
          </span>
          {trailing}
        </div>
      </div>
    );
  },
);
SearchInput.displayName = "SearchInput";

export { SearchInput };
